import { ExternalLink, X } from "lucide-react";
import { useEffect, useRef } from "react";
import { myths, sources, medicalNotice, visualizationNotice } from "../content/sneeze";

interface SourcesDrawerProps {
  open: boolean;
  onClose: () => void;
}

export function SourcesDrawer({ open, onClose }: SourcesDrawerProps) {
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const dialogRef = useRef<HTMLElement>(null);

  useEffect(() => {
    if (!open) return;
    const previouslyFocused = document.activeElement;
    const dialog = dialogRef.current;
    closeButtonRef.current?.focus();
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        event.stopPropagation();
        onClose();
        return;
      }
      if (event.key !== "Tab" || !dialog) return;
      const focusable = Array.from(
        dialog.querySelectorAll<HTMLElement>(
          "a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex='-1'])",
        ),
      );
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (!first || !last) return;
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    dialog?.addEventListener("keydown", onKeyDown);
    return () => {
      dialog?.removeEventListener("keydown", onKeyDown);
      if (previouslyFocused instanceof HTMLElement) previouslyFocused.focus();
    };
  }, [onClose, open]);

  if (!open) return null;

  return (
    <div className="drawer-shell is-open">
      <button className="drawer-backdrop" type="button" onClick={onClose} aria-label="Close sources" />
      <aside ref={dialogRef} className="sources-drawer" role="dialog" aria-modal="true" aria-labelledby="sources-title">
        <header className="sources-drawer__head">
          <div>
            <span>Evidence and transparency</span>
            <h2 id="sources-title">What we know—and what we don’t.</h2>
          </div>
          <button ref={closeButtonRef} type="button" className="icon-button" onClick={onClose} aria-label="Close">
            <X size={18} />
          </button>
        </header>

        <div className="sources-drawer__body">
          <section className="notice-card">
            <strong>Medical scope</strong>
            <p>{medicalNotice}</p>
            <p>{visualizationNotice}</p>
          </section>

          <section className="drawer-section">
            <div className="drawer-section__label">Myth check</div>
            <div className="myth-grid">
              {myths.map((myth) => (
                <article className="myth-card" key={myth.claim}>
                  <span>{myth.verdict}</span>
                  <h3>“{myth.claim}”</h3>
                  <p>{myth.explanation}</p>
                </article>
              ))}
            </div>
          </section>

          <section className="drawer-section">
            <div className="drawer-section__label">Research library</div>
            <div className="source-library">
              {sources.map((source, index) => (
                <a href={source.url} target="_blank" rel="noreferrer" key={source.id}>
                  <span className="source-library__number">{String(index + 1).padStart(2, "0")}</span>
                  <span className="source-library__copy">
                    <small>{source.short}</small>
                    <strong>{source.title}</strong>
                    <em>{source.evidence}</em>
                  </span>
                  <ExternalLink size={15} />
                </a>
              ))}
            </div>
          </section>

          <section className="notice-card notice-card--assets">
            <strong>Model provenance</strong>
            <p>
              Meshes labeled NIH are optimized derivatives of Human Reference Atlas reference objects, licensed CC BY 4.0. Neural, nasal-lining, diaphragm, glottal, and airflow overlays are explicitly labeled explanatory schematics. No generative model created or altered the NIH anatomy.
            </p>
            <a href="https://humanatlas.io/3d-reference-library" target="_blank" rel="noreferrer">
              Open the Human Reference Atlas <ExternalLink size={12} />
            </a>
          </section>
        </div>
      </aside>
    </div>
  );
}
